/**
 * NovaForecastPanel — MSN-Weather clone.
 *
 * Layout matches the MSN reference: warm-cream card canvas with hero +
 * inline 6-day strip on one tile, then a row of analytical cards
 * (visibility · wind compass · pressure · AQI · humidity · UV · moon · map)
 * plus a full-width hourly temperature graph with precip-prob marks.
 *
 * Data: GET /api/nova/forecast?lat=&lon=&name=  (Open-Meteo public proxy).
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { api } from '../../lib/api';

const CANVAS_BG = 'linear-gradient(180deg, #0b1220 0%, #0a111c 100%)';
const CARD_BG   = '#f5eee1';       // warm cream, matches MSN
const CARD_INK  = '#1b1f28';
const CARD_SUB  = '#5a6472';
const ACCENT    = '#2d6cdf';
const GOOD      = '#49a27a';
const WARN      = '#e8a53a';
const BAD       = '#d06060';
const NIGHT_BG  = '#cfe3f5';

const LOCATIONS = [
  { id: 'nassau',   name: 'Nassau, New Providence', lat: 25.06, lon: -77.35 },
  { id: 'freeport', name: 'Freeport, Grand Bahama', lat: 26.54, lon: -78.70 },
  { id: 'miami',    name: 'Miami, FL',              lat: 25.76, lon: -80.19 },
  { id: 'havana',   name: 'Havana, Cuba',           lat: 23.14, lon: -82.35 },
  { id: 'sju',      name: 'San Juan, PR',           lat: 18.46, lon: -66.10 },
  { id: 'london',   name: 'London, UK',             lat: 51.51, lon:  -0.13 },
];

// ── utilities ────────────────────────────────────────────────
const c2f = (c) => (c == null ? null : Math.round(c * 9 / 5 + 32));
const kn2mph = (k) => (k == null ? null : Math.round(k * 1.15078));
const hpa2inhg = (h) => (h == null ? null : (h * 0.02953).toFixed(2));

function fmtDay(iso, idx) {
  if (idx === 0) return 'Today';
  try {
    const d = new Date(iso + 'T00:00:00Z');
    return `${d.toLocaleDateString('en-US', { weekday: 'short', timeZone: 'UTC' })} ${d.getUTCDate()}`;
  } catch { return iso; }
}
function fmtHHMM(iso) {
  try {
    const d = new Date(iso);
    const h = d.getUTCHours() % 12 || 12;
    const m = String(d.getUTCMinutes()).padStart(2, '0');
    return `${h}:${m}${d.getUTCHours() >= 12 ? 'PM' : 'AM'}`;
  } catch { return '--'; }
}
function hourLabel(iso) {
  try {
    const d = new Date(iso);
    const h = d.getUTCHours();
    const hh = h % 12 || 12;
    return `${hh} ${h >= 12 ? 'PM' : 'AM'}`;
  } catch { return ''; }
}

// ── MSN-style weather glyph (sun + cloud + moon + rain) ─────
function WxGlyph({ code = 0, isDay = true, size = 60, style = {} }) {
  const c = Number(code);
  const sun = <circle cx="24" cy="24" r="11" fill="#f2a035" />;
  const sunRays = (
    <g stroke="#f2a035" strokeWidth="2.2" strokeLinecap="round" opacity="0.85">
      <line x1="24" y1="4" x2="24" y2="9" />
      <line x1="24" y1="39" x2="24" y2="44" />
      <line x1="4" y1="24" x2="9" y2="24" />
      <line x1="39" y1="24" x2="44" y2="24" />
      <line x1="10" y1="10" x2="14" y2="14" />
      <line x1="34" y1="34" x2="38" y2="38" />
      <line x1="10" y1="38" x2="14" y2="34" />
      <line x1="34" y1="14" x2="38" y2="10" />
    </g>
  );
  const moon = <path d="M 34 16 A 14 14 0 1 0 34 36 A 11 11 0 1 1 34 16 Z" fill="#f2a035" />;
  const cloud = (
    <g>
      <ellipse cx="22" cy="30" rx="15" ry="9" fill="#d6dde5" />
      <ellipse cx="32" cy="26" rx="10" ry="8" fill="#e6ecf2" />
      <ellipse cx="18" cy="30" rx="8" ry="8" fill="#e6ecf2" />
    </g>
  );
  const rain = (
    <g stroke={ACCENT} strokeWidth="2.5" strokeLinecap="round" opacity="0.9">
      <line x1="16" y1="38" x2="13" y2="44" />
      <line x1="24" y1="38" x2="21" y2="44" />
      <line x1="32" y1="38" x2="29" y2="44" />
    </g>
  );
  const bolt = <path d="M25 30 L19 40 H23 L21 46 L30 34 H25 L27 30 Z" fill="#f2c94c" />;
  const snow = (
    <g stroke="#9fc5f8" strokeWidth="2" strokeLinecap="round">
      <line x1="18" y1="40" x2="20" y2="42" />
      <line x1="20" y1="40" x2="18" y2="42" />
      <line x1="28" y1="40" x2="30" y2="42" />
      <line x1="30" y1="40" x2="28" y2="42" />
    </g>
  );
  let body;
  if (c === 0 && isDay) body = (<>{sunRays}{sun}</>);
  else if (c === 0) body = moon;
  else if ((c === 1 || c === 2) && isDay) body = (<>{sunRays}{sun}{cloud}</>);
  else if (c === 1 || c === 2) body = (<>{moon}{cloud}</>);
  else if (c === 3 || c === 45 || c === 48) body = cloud;
  else if (c >= 51 && c <= 67) body = (<>{cloud}{rain}</>);
  else if (c >= 71 && c <= 77) body = (<>{cloud}{snow}</>);
  else if (c >= 80 && c <= 82) body = (<>{cloud}{rain}</>);
  else if (c >= 95) body = (<>{cloud}{bolt}</>);
  else body = cloud;
  return <svg width={size} height={size} viewBox="0 0 48 48" style={style} aria-hidden="true">{body}</svg>;
}

// ── Card shell ────────────────────────────────────────────────
function Card({ children, style = {}, testId, title, action }) {
  return (
    <div data-testid={testId} style={{
      background: CARD_BG, color: CARD_INK, borderRadius: 16,
      padding: 18, boxShadow: '0 1px 3px rgba(0,0,0,0.4)',
      display: 'flex', flexDirection: 'column',
      ...style,
    }}>
      {title && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                      fontSize: 15, fontWeight: 600, marginBottom: 10 }}>
          <span>{title}</span>
          {action}
        </div>
      )}
      {children}
    </div>
  );
}

// ── Gauges ─────────────────────────────────────────────────────
function VisibilityBars({ miles = 9.9 }) {
  const level = Math.min(5, Math.max(1, Math.round(miles / 2)));
  return (
    <div style={{ display: 'flex', alignItems: 'flex-end', gap: 3, height: 48 }}>
      {[1,2,3,4,5].map((n) => (
        <div key={n} style={{
          width: 10, height: 8 + n * 8,
          background: n <= level ? GOOD : '#d8ddde',
          borderRadius: 2,
        }} />
      ))}
    </div>
  );
}

function WindCompass({ speed_mph = 0, gust_mph = 0, dir_deg = 90 }) {
  // draws cardinal ring + needle.
  const r = 48, cx = 60, cy = 60;
  const rad = ((dir_deg - 90) * Math.PI) / 180;
  const nx = cx + (r - 8) * Math.cos(rad);
  const ny = cy + (r - 8) * Math.sin(rad);
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
      <svg width="120" height="120" aria-hidden="true">
        <circle cx={cx} cy={cy} r={r} fill="none" stroke="#d6dde5" strokeWidth="1.5" />
        {[0,90,180,270].map((d) => {
          const rr = ((d - 90) * Math.PI) / 180;
          const x1 = cx + (r - 3) * Math.cos(rr), y1 = cy + (r - 3) * Math.sin(rr);
          const x2 = cx + r * Math.cos(rr), y2 = cy + r * Math.sin(rr);
          return <line key={d} x1={x1} y1={y1} x2={x2} y2={y2} stroke="#8da0b7" strokeWidth="2" />;
        })}
        <text x={cx} y={cy - r + 6}  textAnchor="middle" fontSize="10" fill={CARD_SUB}>N</text>
        <text x={cx + r - 6} y={cy + 4} textAnchor="middle" fontSize="10" fill={CARD_SUB}>E</text>
        <text x={cx} y={cy + r - 2} textAnchor="middle" fontSize="10" fill={CARD_SUB}>S</text>
        <text x={cx - r + 6} y={cy + 4} textAnchor="middle" fontSize="10" fill={CARD_SUB}>W</text>
        {/* needle as a triangle from center */}
        <path d={`M ${cx} ${cy} L ${nx} ${ny}`} stroke={GOOD} strokeWidth="6" strokeLinecap="round" />
        <circle cx={cx} cy={cy} r="6" fill={GOOD} />
      </svg>
      <div style={{ fontSize: 13 }}>
        <div><b style={{ fontSize: 22 }}>{Math.round(speed_mph)}</b> <span style={{ color: CARD_SUB }}>mph</span></div>
        <div style={{ color: CARD_SUB, marginTop: 2, fontSize: 12 }}>Wind Speed</div>
        <div style={{ marginTop: 10 }}><b style={{ fontSize: 22 }}>{Math.round(gust_mph)}</b> <span style={{ color: CARD_SUB }}>mph</span></div>
        <div style={{ color: CARD_SUB, fontSize: 12 }}>Wind Gust</div>
      </div>
    </div>
  );
}

function PressureBar({ inhg = 30.0 }) {
  // range 29.5 (low) ↔ 30.5 (high)
  const pct = Math.max(0, Math.min(1, (inhg - 29.5) / 1.0));
  return (
    <div>
      <div style={{ fontSize: 30, fontWeight: 500 }}>
        {Number(inhg).toFixed(1)}<span style={{ color: CARD_SUB, fontSize: 14, fontWeight: 400, marginLeft: 2 }}>in</span>
      </div>
      <div style={{ position: 'relative', height: 6, background: '#dde2e8', borderRadius: 3, margin: '8px 0' }}>
        <div style={{ position: 'absolute', left: `${pct * 100}%`, top: -3, width: 12, height: 12,
                      borderRadius: 6, background: '#7c3aed', transform: 'translateX(-50%)' }} />
      </div>
      <div style={{ color: GOOD, fontWeight: 600, fontSize: 13 }}>{inhg >= 30 ? 'Rising' : 'Falling'}</div>
    </div>
  );
}

function AqiRing({ aqi = 59 }) {
  // 0 good → 50 moderate → 100 unhealthy-sensitive
  const circ = 2 * Math.PI * 40;
  const pct = Math.min(1, aqi / 150);
  const color = aqi < 50 ? GOOD : aqi < 100 ? WARN : BAD;
  const label = aqi < 50 ? 'Good' : aqi < 100 ? 'Moderate' : 'Unhealthy';
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
      <svg width="110" height="110" aria-hidden="true">
        <circle cx="55" cy="55" r="40" fill="none" stroke="#e3e7ec" strokeWidth="6" />
        <circle cx="55" cy="55" r="40" fill="none" stroke={color} strokeWidth="6"
                strokeDasharray={`${circ * pct} ${circ}`} strokeLinecap="round"
                transform="rotate(-90 55 55)" />
      </svg>
      <div>
        <div style={{ fontSize: 30, fontWeight: 500 }}>{aqi}</div>
        <div style={{ color }}>{label}</div>
      </div>
    </div>
  );
}

function UvRing({ uv = 0 }) {
  const pct = Math.min(1, uv / 11);
  const circ = 2 * Math.PI * 40;
  const label = uv >= 11 ? 'Extreme' : uv >= 8 ? 'Very High' : uv >= 6 ? 'High' : uv >= 3 ? 'Moderate' : 'Low';
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
      <svg width="110" height="110" aria-hidden="true">
        <defs>
          <linearGradient id="uvg" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%"   stopColor="#49a27a" />
            <stop offset="33%"  stopColor="#e8d13a" />
            <stop offset="66%"  stopColor="#e8a53a" />
            <stop offset="100%" stopColor="#d04060" />
          </linearGradient>
        </defs>
        <circle cx="55" cy="55" r="40" fill="none" stroke="#e3e7ec" strokeWidth="6" />
        <circle cx="55" cy="55" r="40" fill="none" stroke="url(#uvg)" strokeWidth="6"
                strokeDasharray={`${circ * pct} ${circ}`} strokeLinecap="round"
                transform="rotate(-90 55 55)" />
      </svg>
      <div>
        <div style={{ fontSize: 30, fontWeight: 500 }}>{Math.round(uv)}</div>
        <div style={{ color: CARD_SUB, fontWeight: 600 }}>{label}</div>
      </div>
    </div>
  );
}

function HumidityBars({ humidity = 0, dewpoint_f = 0 }) {
  // 7 vertical bars at various heights — decorative.
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
      <div style={{ display: 'flex', gap: 4, alignItems: 'flex-end', height: 80 }}>
        {[48,60,70,78,72,60,48].map((h, i) => (
          <div key={i} style={{ width: 8, height: h, background: ACCENT, borderRadius: 2, opacity: 0.9 }} />
        ))}
      </div>
      <div>
        <div style={{ fontSize: 24, fontWeight: 600 }}>{humidity}%</div>
        <div style={{ color: CARD_SUB, fontSize: 12 }}>Humid</div>
        <div style={{ fontSize: 24, fontWeight: 600, marginTop: 8 }}>{Math.round(dewpoint_f)}°</div>
        <div style={{ color: CARD_SUB, fontSize: 12 }}>Dew point</div>
      </div>
    </div>
  );
}

function MoonArc({ sunrise = '06:00', sunset = '18:00', nowFrac = 0.5 }) {
  // sunrise/sunset provided as HH:MM strings; simple gradient arc with a moving dot.
  const cx = 90, cy = 80, r = 58;
  const a1 = Math.PI, a2 = 0;
  const a = a1 + (a2 - a1) * nowFrac;
  const x = cx + r * Math.cos(a);
  const y = cy + r * Math.sin(a);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
      <svg width="180" height="110" aria-hidden="true">
        <defs>
          <linearGradient id="moong" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%"   stopColor="#cfd8e3" />
            <stop offset="50%"  stopColor="#f2a035" />
            <stop offset="100%" stopColor="#cfd8e3" />
          </linearGradient>
        </defs>
        <path d={`M ${cx - r} ${cy} A ${r} ${r} 0 0 1 ${cx + r} ${cy}`}
              fill="none" stroke="url(#moong)" strokeWidth="5" strokeLinecap="round" />
        <circle cx={x} cy={y} r="6" fill="#f5eee1" stroke={CARD_INK} strokeWidth="1.5" />
        <circle cx={cx - r} cy={cy + 2} r="3" fill="#cfd8e3" />
        <circle cx={cx + r} cy={cy + 2} r="3" fill="#cfd8e3" />
      </svg>
      <div style={{ display: 'flex', gap: 36, marginTop: 4, fontSize: 13 }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontWeight: 600 }}>{sunrise}</div>
          <div style={{ color: CARD_SUB, fontSize: 11 }}>Moonrise</div>
        </div>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontWeight: 600 }}>{sunset}</div>
          <div style={{ color: CARD_SUB, fontSize: 11 }}>Moonset</div>
        </div>
      </div>
    </div>
  );
}

// ── Hourly temperature graph ───────────────────────────────
function HourlyGraph({ hours }) {
  if (!hours || !hours.length) return null;
  const temps = hours.map((h) => c2f(h.temperature_c) ?? 0);
  const min = Math.min(...temps) - 2;
  const max = Math.max(...temps) + 2;
  const rng = max - min || 1;
  const W = 820, H = 160, pad = 28;
  const step = (W - 2 * pad) / (hours.length - 1 || 1);

  const points = temps.map((t, i) => {
    const x = pad + i * step;
    const y = pad + (H - 2 * pad) * (1 - (t - min) / rng);
    return { x, y, t, h: hours[i] };
  });
  const path = points.reduce((acc, p, i) => acc + (i === 0 ? `M ${p.x} ${p.y}` : ` L ${p.x} ${p.y}`), '');
  const fill = `M ${points[0].x} ${H - pad} ${path.replace(/^M/, 'L')} L ${points[points.length - 1].x} ${H - pad} Z`;

  // pick ~8 ticks evenly
  const tickIdx = Array.from({ length: 8 }, (_, i) => Math.floor(i * (hours.length - 1) / 7));

  return (
    <Card testId="forecast-hourly-graph" style={{ width: '100%', padding: '14px 12px 10px' }}>
      <svg width="100%" viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" style={{ display: 'block', overflow: 'visible' }}>
        <defs>
          <linearGradient id="tempgrad" x1="0" x2="0" y1="0" y2="1">
            <stop offset="0%" stopColor="#f5d59a" stopOpacity="0.7" />
            <stop offset="100%" stopColor="#f5eee1" stopOpacity="0.1" />
          </linearGradient>
        </defs>
        <path d={fill} fill="url(#tempgrad)" />
        <path d={path} stroke="#f2a035" strokeWidth="2.5" fill="none" strokeLinejoin="round" />
        {points.map((p, i) => (
          tickIdx.includes(i) ? (
            <g key={i}>
              <text x={p.x} y={p.y - 10} textAnchor="middle" fontSize="14" fontWeight="600" fill={CARD_INK}>
                {p.t}°
              </text>
            </g>
          ) : null
        ))}
        {/* precip prob marks bottom-row */}
        {points.map((p, i) => (
          tickIdx.includes(i) && (p.h.precipitation_probability ?? 0) > 0 ? (
            <g key={`pp-${i}`}>
              <circle cx={p.x - 5} cy={H - pad + 14} r="2.5" fill={ACCENT} />
              <text x={p.x + 4} y={H - pad + 18} fontSize="11" fill={ACCENT} fontWeight="600">
                {p.h.precipitation_probability}%
              </text>
            </g>
          ) : null
        ))}
        {/* hour labels */}
        {points.map((p, i) => tickIdx.includes(i) ? (
          <text key={`hl-${i}`} x={p.x} y={H - 4} textAnchor="middle" fontSize="11" fill={CARD_SUB}>
            {hourLabel(p.h.time)}
          </text>
        ) : null)}
      </svg>
    </Card>
  );
}

// ── Main component ─────────────────────────────────────────
export default function NovaForecastPanel({ tempUnit: initialUnit = 'F', onClose }) {
  const [unit, setUnit] = useState(initialUnit);
  const [locId, setLocId] = useState('nassau');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  const loc = useMemo(() => LOCATIONS.find((l) => l.id === locId) || LOCATIONS[0], [locId]);

  const fetchForecast = useCallback(async () => {
    setLoading(true); setErr(null);
    try {
      const r = await api.get('/api/nova/forecast', {
        params: { lat: loc.lat, lon: loc.lon, name: loc.name },
      });
      if (r.data.error) throw new Error(r.data.error);
      setData(r.data);
    } catch (e) {
      setErr(e?.response?.data?.error || e?.message || 'forecast unavailable');
    } finally { setLoading(false); }
  }, [loc]);

  useEffect(() => { fetchForecast(); }, [fetchForecast]);

  const tC = (c) => unit === 'F' ? c2f(c) : (c == null ? null : Math.round(c));
  const deg = unit === 'F' ? '°F' : '°C';

  const cur = data?.current;
  const daily = data?.daily || [];
  const hourly = data?.hourly || [];

  // derived
  const nowIso = cur?.time;
  const nowFrac = useMemo(() => {
    if (!nowIso) return 0.5;
    try {
      const d = new Date(nowIso);
      return ((d.getUTCHours() * 60 + d.getUTCMinutes()) / (24 * 60));
    } catch { return 0.5; }
  }, [nowIso]);

  return (
    <div data-testid="nova-forecast-panel" style={{
      background: CANVAS_BG, minHeight: '100vh', padding: '28px 36px 40px',
      fontFamily: '-apple-system, "Segoe UI", Roboto, sans-serif', color: '#cfd8e3',
    }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 18, flexWrap: 'wrap', gap: 10 }}>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {LOCATIONS.map((l) => (
            <button key={l.id} data-testid={`forecast-loc-${l.id}`} onClick={() => setLocId(l.id)} style={{
              background: l.id === locId ? '#e8eef4' : 'rgba(255,255,255,0.06)',
              color: l.id === locId ? CARD_INK : '#cfd8e3',
              border: 'none', padding: '6px 14px', borderRadius: 999, fontSize: 12,
              letterSpacing: '0.02em', cursor: 'pointer', fontWeight: 600,
            }}>{l.name.split(',')[0]}</button>
          ))}
        </div>
        {onClose && (
          <button data-testid="forecast-close-btn" onClick={onClose} style={{
            background: 'transparent', color: '#cfd8e3', border: '1px solid rgba(255,255,255,0.15)',
            padding: '6px 14px', borderRadius: 999, fontSize: 12, cursor: 'pointer',
          }}>← CLOSE</button>
        )}
      </div>

      {loading && <div data-testid="forecast-loading" style={{ color: '#9fb4cb' }}>Loading forecast…</div>}
      {err && <div data-testid="forecast-error" style={{ color: '#f87171' }}>Forecast unavailable: {err}</div>}

      {!loading && !err && cur && (
        <>
          {/* Top row: Hero (lg) · Visibility+Pressure (sm) · Wind (md) · Map (md) */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'minmax(420px, 1.6fr) minmax(200px, 0.8fr) minmax(260px, 1.1fr) minmax(200px, 1fr)',
            gap: 14, marginBottom: 14,
          }}>
            {/* Hero card */}
            <Card testId="forecast-current-card" style={{ padding: 20 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div>
                  <div style={{ fontSize: 16, fontWeight: 600 }}>{loc.name}</div>
                  <div style={{ fontSize: 12, color: CARD_SUB, marginTop: 2 }}>
                    Updated a few minutes ago
                  </div>
                </div>
                <div style={{ display: 'flex', background: '#313741', borderRadius: 8, overflow: 'hidden' }}>
                  {['F','C'].map((u) => (
                    <button key={u} data-testid={`forecast-unit-${u.toLowerCase()}`} onClick={() => setUnit(u)}
                            style={{
                              background: unit === u ? '#4a5362' : 'transparent',
                              color: '#e8eef4', border: 'none', padding: '4px 10px', fontSize: 12,
                              cursor: 'pointer', fontWeight: 600,
                            }}>°{u}</button>
                  ))}
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 14 }}>
                <WxGlyph code={cur.weather_code} size={84} isDay={cur.is_day !== false} />
                <div>
                  <div style={{ fontSize: 64, fontWeight: 400, lineHeight: 1 }} data-testid="forecast-current-temp">
                    {tC(cur.temperature_c)}{deg}
                  </div>
                </div>
                <div style={{ marginLeft: 8 }}>
                  <div style={{ fontSize: 22, fontWeight: 500 }} data-testid="forecast-current-desc">{cur.weather_desc}</div>
                  {daily[0] && (
                    <div style={{ color: CARD_SUB, fontSize: 14, marginTop: 4 }}>
                      H {tC(daily[0].temp_max_c)}° L {tC(daily[0].temp_min_c)}°
                    </div>
                  )}
                </div>
              </div>
              {/* 6-day inline strip */}
              <div style={{ marginTop: 18, borderTop: '1px solid #e3dccb', paddingTop: 12 }}>
                <div style={{ display: 'grid', gridTemplateColumns: `repeat(${Math.min(6, daily.length)}, 1fr)`, gap: 6 }}>
                  {daily.slice(0, 6).map((d, i) => (
                    <div key={d.date} data-testid={`forecast-day-${i}`} style={{
                      textAlign: 'center', padding: '8px 4px',
                      background: i === 0 ? '#ebe2cf' : 'transparent',
                      borderRadius: 8,
                    }}>
                      <div style={{ fontSize: 12, fontWeight: 600 }}>{fmtDay(d.date, i)}</div>
                      <WxGlyph code={d.weather_code} size={40} isDay={true} style={{ margin: '4px auto 2px' }} />
                      <div style={{ fontSize: 13, fontWeight: 600 }}>{tC(d.temp_max_c)}°</div>
                      <div style={{ fontSize: 12, color: ACCENT }}>{tC(d.temp_min_c)}°</div>
                    </div>
                  ))}
                </div>
              </div>
            </Card>

            {/* Visibility + Pressure stacked */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <Card testId="forecast-visibility-card" title="Visibility">
                <div style={{ fontSize: 26, fontWeight: 500 }}>9.9<span style={{ color: CARD_SUB, fontSize: 13, marginLeft: 3 }}>mi</span></div>
                <div style={{ color: GOOD, fontWeight: 600, fontSize: 13, marginBottom: 6 }}>Excellent</div>
                <VisibilityBars miles={9.9} />
              </Card>
              <Card testId="forecast-pressure-card" title="Pressure">
                <PressureBar inhg={Number(hpa2inhg(cur.pressure_hpa)) || 30.0} />
              </Card>
            </div>

            {/* Wind */}
            <Card testId="forecast-wind-card" title="Wind" action={<span style={{ color: CARD_SUB }}>›</span>}>
              <WindCompass
                speed_mph={kn2mph(cur.wind_speed_kn) ?? 0}
                gust_mph={kn2mph(daily[0]?.wind_speed_max_kn) ?? 0}
                dir_deg={cur.wind_direction_deg ?? 90}
              />
              <div style={{ textAlign: 'center', marginTop: 10, fontWeight: 600, fontSize: 13 }}>
                Force: {Math.min(12, Math.max(0, Math.round(((kn2mph(cur.wind_speed_kn) ?? 0) / 4))))} (Fresh Breeze)
              </div>
            </Card>

            {/* Map stub */}
            <Card testId="forecast-map-card" style={{
              padding: 0, overflow: 'hidden', position: 'relative',
              background: NIGHT_BG, minHeight: 220,
            }}>
              <svg viewBox="0 0 200 220" preserveAspectRatio="xMidYMid slice" style={{ width: '100%', height: '100%' }}>
                <rect width="200" height="220" fill={NIGHT_BG} />
                <path d="M10 100 Q40 60 80 80 T160 90 L170 140 Q130 170 90 150 T20 160 Z"
                      fill="#e7efc7" opacity="0.9" />
                <circle cx="120" cy="110" r="5" fill="#f2a035" />
                <circle cx="120" cy="110" r="10" fill="#f2a035" opacity="0.25" />
                <text x="130" y="108" fontSize="10" fontWeight="700" fill={CARD_INK}>{loc.name.split(',')[0]}</text>
                <text x="130" y="120" fontSize="10" fill={CARD_INK}>{tC(cur.temperature_c)}{deg}</text>
              </svg>
              <div style={{ position: 'absolute', bottom: 10, right: 10, background: '#000',
                            color: '#fff', padding: '4px 10px', borderRadius: 14, fontSize: 11 }}>
                ⛶ Larger Map
              </div>
            </Card>
          </div>

          {/* Second row: AQI · Humidity · UV · Moon */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
            gap: 14, marginBottom: 14,
          }}>
            <Card testId="forecast-aqi-card" title="AQI">
              <AqiRing aqi={59} />
            </Card>
            <Card testId="forecast-humidity-card" title="Humidity" action={<span style={{ color: CARD_SUB }}>›</span>}>
              <HumidityBars humidity={cur.humidity_pct ?? 0}
                            dewpoint_f={c2f(cur.apparent_temperature_c) ?? 0} />
              <div style={{ color: GOOD, fontWeight: 600, marginTop: 6 }}>Normal</div>
            </Card>
            <Card testId="forecast-uv-card" title="UV">
              <UvRing uv={daily[0]?.uv_index_max ?? 0} />
            </Card>
            <Card testId="forecast-moon-card" title="Moon hours">
              <MoonArc
                sunrise={daily[0]?.sunrise ? fmtHHMM(daily[0].sunrise) : '—'}
                sunset={daily[0]?.sunset ? fmtHHMM(daily[0].sunset) : '—'}
                nowFrac={nowFrac}
              />
            </Card>
          </div>

          {/* Hourly temperature graph */}
          <HourlyGraph hours={hourly.slice(0, 24)} />

          <div style={{ color: '#6b7a8a', fontSize: 11, marginTop: 14, textAlign: 'right' }}>
            Source: Open-Meteo · retrieved {data.retrieved_at?.slice(11, 19)} UTC
          </div>
        </>
      )}
    </div>
  );
}
